import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { type ResourceItem } from '@forecastify/shared';
import { anchorTo, Badge, Icon, Tooltip, useAnchorRect } from '@forecastify/ui';
import { useDuration, type Duration } from '../../duration';
import { useT } from '../../i18n';
import { contractTypeLabel } from '../contracts/contract-type-label';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ WHAT ONE LINE ITEM IS — round 5 items 28 and 29.                     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The mockup puts an info button on every resource row and opens this beside it; the app had the
 * button nowhere and the figures nowhere. *"A sorról hiányzik az info gomb ami quick summary az
 * adott sorhoz. Ez a mockupon helyesen szerepel."*
 *
 * ⚠️⚠️ **IT IS A COMPONENT BECAUSE ITEM 29 ASKS FOR THE SAME EIGHT FIGURES AGAIN.** The global
 * button beside the filter opens a table with one row per line item and these columns — *"don't
 * invent new elements, use existing ones"*. A pop-over that built its own layout inline would be
 * copied into that table and the two would drift on the first change.
 *
 * ⚠️ **Every figure comes off the row it is opened from.** `resource.ts` carries them so no read
 * happens on hover — `resource.service.ts` argues why at the query.
 */

/** The bar and its percentage. ⚠️ Null estimate means there is no ratio, not a ratio of zero. */
function ratioOf(item: ResourceItem): number | null {
  const estimate = Number(item.estimateHours);
  if (!Number.isFinite(estimate) || estimate <= 0) return null;
  return Math.round((Number(item.plannedHours) / estimate) * 100);
}

/** One label-and-figure line. The pair is the whole shape of this panel. */
function Line({
  label,
  value,
  muted = false,
}: {
  readonly label: string;
  readonly value: string;
  /** ⚠️ A figure of nothing is drawn quietly — it is true, and it is not the point of the row. */
  readonly muted?: boolean;
}): ReactNode {
  return (
    <span className="flex items-baseline justify-between gap-3 border-b py-1.5 last:border-0">
      <span
        className={`text-[11.5px] ${muted ? 'text-muted-foreground/60' : 'text-muted-foreground'}`}
      >
        {label}
      </span>
      <span className={`text-[12px] tabular-nums ${muted ? 'text-muted-foreground/60' : ''}`}>
        {value}
      </span>
    </span>
  );
}

/**
 * ⚠️ **Hours or an em-dash, and `0:00` is not an em-dash.** A booking with nothing logged has
 * logged nothing, which is a fact; the dash is for a figure that does not apply — `Túlóra` on an
 * ordinary booking, a window with no end date.
 */
function hoursOrDash(value: string, fmt: Duration['fmt']): string {
  return Number(value) === 0 ? '–' : fmt(Number(value));
}

export function LineItemSummary({
  item,
  onOpen,
}: {
  readonly item: ResourceItem;
  /** Absent where the reader cannot open it — the link is then not drawn. */
  readonly onOpen?: () => void;
}): ReactNode {
  const t = useT();
  const { fmt } = useDuration();
  const ratio = ratioOf(item);

  return (
    <div className="flex w-[260px] flex-col">
      <span className="flex items-start gap-2 border-b pb-2">
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[12.5px] font-semibold">{item.lineItemName}</span>
          {item.contractIdentifier === null ? null : (
            <span className="text-muted-foreground block truncate text-[11px]">
              {item.contractIdentifier}
            </span>
          )}
        </span>
        {/* ⚠️ The contract's kind, as the mockup's `Fix díjas` chip — absent rather than blank
            where the row does not know it. */}
        {item.contractType === null ? null : (
          <Badge variant="secondary">{contractTypeLabel(t, item.contractType)}</Badge>
        )}
      </span>

      {/**
       * ⚠️⚠️ **THE RATIO IS THE HEADLINE, WHICH IS WHY IT HAS A BAR AND THE REST DO NOT.** It is
       * the one figure that answers *is this booking in trouble* without arithmetic.
       *
       * ⚠️ **Over 100% still draws a full bar**, and the number beside it is what says by how much.
       * A bar that overflowed its track would be drawing outside the panel to say what the figure
       * already says.
       */}
      {ratio === null ? null : (
        <span className="flex flex-col gap-1 border-b py-2">
          <span className="flex items-baseline justify-between gap-3">
            <span className="text-muted-foreground text-[11px] tracking-wide uppercase">
              {t('resource.tip.ratio')}
            </span>
            <span className="text-[12px] font-medium tabular-nums">{`${String(ratio)}%`}</span>
          </span>
          <span className="bg-muted h-1.5 overflow-hidden rounded-full">
            <span
              className={`block h-full rounded-full ${ratio > 100 ? 'bg-destructive' : 'bg-primary'}`}
              style={{ width: `${String(Math.min(100, Math.max(0, ratio)))}%` }}
            />
          </span>
        </span>
      )}

      <span className="flex flex-col">
        {/* ⚠️ The booking's window, not the line item's — `resource.ts` records the distinction. */}
        <Line label={t('contract.column.startsOn')} value={item.fromDate ?? '–'} />
        <Line label={t('contract.column.endsOn')} value={item.toDate ?? '–'} />
        <Line label={t('contract.column.estimate')} value={hoursOrDash(item.estimateHours, fmt)} />
        <Line label={t('resource.tip.plan')} value={hoursOrDash(item.plannedHours, fmt)} />
        <Line
          label={t('resource.tip.overtime')}
          value={hoursOrDash(item.overtimeHours, fmt)}
          muted={Number(item.overtimeHours) === 0}
        />
        <Line label={t('contract.column.tracked')} value={hoursOrDash(item.trackedHours, fmt)} />
        <Line label={t('resource.tip.remaining')} value={hoursOrDash(item.remainingHours, fmt)} />
      </span>

      {onOpen === undefined ? null : (
        <button
          type="button"
          onClick={onOpen}
          className="hover:bg-accent/40 -mx-1 mt-1 flex items-center gap-1.5 rounded-md px-1 py-1.5 text-left text-[12px] transition-colors"
        >
          <span className="flex-1">{t('resource.tip.open')}</span>
          <Icon name="chevron-right" size={13} className="text-muted-foreground" />
        </button>
      )}
    </div>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE ROW'S INFO CONTROL — item 28, second pass.                       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *"The hint we added per row opens upwards, has bad clipping, and the Open line item from the
 * bottom is missing. I need this to function EXACTLY the same [as the mockup]. Hovering brings up
 * a tooltip, clicking brings up the summary popover, and it has a link to open the lineitem."*
 *
 * ⚠️⚠️ **THE FIRST PASS PUT THE WHOLE PANEL INSIDE A `Tooltip`, AND ALL THREE FAULTS FOLLOW FROM
 * THAT.** A tooltip is sized and placed for a sentence: it flipped above the row because a 260px
 * panel does not fit under one in a scrolling grid, it was clipped by the grid's own overflow, and
 * the link could not be there at all — a surface that closes when the pointer leaves it cannot
 * hold something you have to travel to press.
 *
 * So the two readings are two controls, as the mockup has them: a `Tooltip` on the button says
 * *what the button is*, and a click opens the panel as a real pop-over — portalled out of the
 * grid so nothing clips it, anchored under the button, dismissed by Escape or a click outside.
 * The same machinery `allocation-cell.tsx` uses, for the same reason.
 */
export function LineItemInfo({
  item,
  label,
  onOpen,
}: {
  readonly item: ResourceItem;
  /** The button's own name — already translated. */
  readonly label: string;
  /** Absent where the address cannot be built; the panel then draws no link. */
  readonly onOpen?: () => void;
}): ReactNode {
  const anchor = useRef<HTMLButtonElement | null>(null);
  const surface = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const { rect } = useAnchorRect(open, anchor);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onDown = (event: MouseEvent): void => {
      const node = event.target;
      if (!(node instanceof Node)) return;
      if (surface.current?.contains(node) === true) return;
      if (anchor.current?.contains(node) === true) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  return (
    <>
      <Tooltip content={label}>
        <button
          ref={anchor}
          type="button"
          aria-label={label}
          aria-expanded={open}
          /* 3 Nov 2026 · F4.7 · it reads as a control: the pointer and a rounded highlight. */
          className="text-muted-foreground hover:text-foreground hover:bg-accent flex size-5 shrink-0 cursor-pointer items-center justify-center rounded transition-colors"
          onClick={() => {
            setOpen((current) => !current);
          }}
        >
          <Icon name="circle-help" size={12} className="shrink-0" />
        </button>
      </Tooltip>

      {!open || typeof document === 'undefined'
        ? null
        : createPortal(
            <div
              ref={surface}
              role="dialog"
              aria-label={label}
              className="bg-popover text-popover-foreground shadow-pop border-border rounded-lg border p-3"
              style={anchorTo(rect, { side: 'bottom', align: 'start', width: 284 })}
            >
              <LineItemSummary item={item} {...(onOpen === undefined ? {} : { onOpen })} />
            </div>,
            document.body,
          )}
    </>
  );
}
